/**
 * The single origin every admin request goes to.
 *
 * WHY THIS MODULE EXISTS
 * `services/api.ts` and `utils/productMediaUrl.ts` each carried their own copy of
 * `import.meta.env.VITE_API_URL || 'https://api.intelligent-automation.net'`, and the
 * second one's comment said the duplication existed "so a media URL can never point at
 * a different environment than the API calls do". Two expressions of the same rule is
 * exactly how they come to disagree; one exported constant cannot.
 *
 * WHY THE DEV BRANCH THROWS
 * That fallback is correct for a production build and dangerous everywhere else. Vite
 * loads no env file in dev unless `.env.development` exists, so for as long as the repo
 * lacked one, the documented `npm run dev` silently pointed the admin console — which
 * has destructive endpoints — at PRODUCTION. Cognito being unconfigured blocked the
 * login, not the requests.
 *
 * `.env.development` now ships, so the fallback should be unreachable in dev. Throwing
 * rather than defaulting is what keeps it that way: if the file is deleted, renamed, or
 * missing from a fresh clone, the app fails loudly at boot instead of quietly operating
 * on live data. A blank screen with this message is a far cheaper failure.
 */
const PRODUCTION_API = 'https://api.intelligent-automation.net';

function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL;
  if (configured) return configured;

  if (import.meta.env.DEV) {
    throw new Error(
      'VITE_API_URL is not set, and in development there is no safe default — the ' +
        'fallback is PRODUCTION, and this console has destructive endpoints.\n' +
        'Restore .env.development (it points at staging), or run `npm run start:local-api` ' +
        'for a backend on http://localhost:3001.',
    );
  }

  return PRODUCTION_API;
}

export const API_BASE_URL: string = resolveApiBaseUrl();
